import { useContext, useEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import { createCardElement } from '@appcharge/ac-payment-element-js';
import { CheckoutContext } from '@/context/CheckoutContext';
import { useTranslate } from '@/hooks/useTranslate';
import { logError } from '@/utils/logger';
import { getLogInfoData } from '@/utils/logs';
import { ErrorLogs } from '@/constants/logs';
import {
  APPCHARGE_PAYMENT_ELEMENT_FIELD_STYLES,
  APPCHARGE_PAYMENT_ELEMENT_FIELD_URLS,
} from '@/constants/appchargePaymentElement';
import { getAppchargePaymentElementFieldErrorMessage } from '@/utils/appchargePaymentElement';
import type { AppchargePaymentElementCard, AppchargePaymentElementFieldEvent } from '@/types/appchargePaymentElement';

export type FieldMetadata = { isValid: boolean; error: string; ref: RefObject<HTMLDivElement> };

export const useAppchargeCard = () => {
  const context = useContext(CheckoutContext);
  const {
    actions: { setError: setCheckoutError },
    card: { setCardElementRef },
  } = context;
  const { t } = useTranslate();

  const [isLoading, setIsLoading] = useState(true);
  const [cardElement, setCardElement] = useState<AppchargePaymentElementCard | null>(null);

  const numberFieldRef = useRef<HTMLDivElement>(null);
  const expiryFieldRef = useRef<HTMLDivElement>(null);
  const cvvFieldRef = useRef<HTMLDivElement>(null);
  const [cardNumberMetadata, setCardNumberMetadata] = useState<FieldMetadata>({
    error: '',
    isValid: false,
    ref: numberFieldRef,
  });
  const [expiryMetadata, setExpiryMetadata] = useState<FieldMetadata>({
    error: '',
    isValid: false,
    ref: expiryFieldRef,
  });
  const [cvvMetadata, setCvvMetadata] = useState<FieldMetadata>({
    error: '',
    isValid: false,
    ref: cvvFieldRef,
  });
  const isInitialized = useRef(false);
  const cardElementRef = useRef<AppchargePaymentElementCard | null>(null);
  const contextRef = useRef(context);
  contextRef.current = context;

  useEffect(() => {
    const numberEl = numberFieldRef.current;
    const expiryEl = expiryFieldRef.current;
    const cvvEl = cvvFieldRef.current;

    if (!numberEl || !expiryEl || !cvvEl) return;
    if (isInitialized.current) return;
    isInitialized.current = true;

    let card: AppchargePaymentElementCard | null = null;
    try {
      card = createCardElement({
        type: 'card',
        fields: { number: numberEl, expiry: expiryEl, cvv: cvvEl },
        fieldUrls: APPCHARGE_PAYMENT_ELEMENT_FIELD_URLS,
        styles: APPCHARGE_PAYMENT_ELEMENT_FIELD_STYLES,
      });
      setCardElement(card);
      cardElementRef.current = card;
      setCardElementRef(cardElementRef);

      card.ready
        .then(() => setIsLoading(false))
        .catch(() => {
          logError(ErrorLogs.appchargePaymentElementCardFormError, getLogInfoData(contextRef.current));
          setCheckoutError(t('somethingWentWrong'));
          setIsLoading(false);
        });
    } catch {
      logError(ErrorLogs.appchargePaymentElementCardFormError, getLogInfoData(contextRef.current));
      setCheckoutError(t('somethingWentWrong'));
      setIsLoading(false);
      isInitialized.current = false;
    }

    return () => {
      card?.destroy();
      // setCardElement(null);
      isInitialized.current = false;
    };
  }, [setCardElementRef]);

  const validateField = ({ emittedBy, state }: AppchargePaymentElementFieldEvent) => {
    const nextState = {
      error: getAppchargePaymentElementFieldErrorMessage(emittedBy, state, t),
      isValid: state.isValid,
    };
    switch (emittedBy) {
      case 'number':
        setCardNumberMetadata({
          ref: numberFieldRef,
          ...nextState,
        });
        break;
      case 'expiry':
        setExpiryMetadata({
          ref: expiryFieldRef,
          ...nextState,
        });
        break;
      case 'cvv':
        setCvvMetadata({
          ref: cvvFieldRef,
          ...nextState,
        });
        break;
    }
  };

  useEffect(() => {
    if (cardElement) {
      cardElement.on('validityChange', validateField);
      cardElement.on('blur', validateField);
    }
  }, [cardElement]);

  return {
    isCardFormValid: cardNumberMetadata.isValid && expiryMetadata.isValid && cvvMetadata.isValid,
    isLoading,
    cardNumberMetadata,
    expiryMetadata,
    cvvMetadata,
    numberFieldRef,
    expiryFieldRef,
    cvvFieldRef,
  };
};
